import React from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import { addDaysToDate } from '@/util';
import { createInvoice } from '@/modules/dataWrangler/dataWrangler';
import { ConvertIconWhite } from '@/resources/icons';
import * as posthogManager from '@/modules/posthog/posthogManager';
import { useSubscription } from '../useSubscription';
import { useToastNotification } from '../useToastNotification';

const useStyles = makeStyles(() => ({
    convertIcon: {
        '& path:nth-child(3)': {
            filter: 'brightness(60%)'
        },
        '& path:last-child': {
            filter: 'brightness(60%)'
        },
        '& circle': {
            stroke: '#00834E',
            filter: 'brightness(60%)',
            fill: '#FFF'
        }
    }
}));

export const useConvertEstimate = () => {
    const navigate = useNavigate();
    const activeBusiness = useSelector((state) => state?.user?.activeBusiness);
    const subscriptionData = useSubscription();
    const { triggerToast } = useToastNotification();
    const classes = useStyles();

    const invoiceTerms = activeBusiness?.documentCustomizations?.invoiceTerms;
    const invoiceDefaultNotes =
        activeBusiness?.documentCustomizations?.invoiceNotes;

    return async (estimateData) => {
        const { documentState, customLabel } = estimateData;

        const subInfo = subscriptionData?.components?.document;
        const blockEstimateCreation =
            !subInfo?.enabled ||
            (subInfo?.limited && subInfo?.amountRemaining < 1);

        // If the user has no more documents remaining, force them to subscription page.
        if (blockEstimateCreation) {
            navigate('/settings/subscription');
            return false;
        }

        if (documentState !== 'approved') {
            return false;
        }

        const newDueDate = addDaysToDate(Date.now(), invoiceTerms || 7);

        const convertedEstimateData = {
            ...estimateData,
            customLabel: undefined,
            documentState: 'draft',
            documentType: 'invoice',
            documentId: undefined,
            dueDate: newDueDate.toISOString(),
            details: estimateData.details || invoiceDefaultNotes || ''
        };

        await posthogManager.documentStarted('invoice');
        const newInvoiceObject = await createInvoice(convertedEstimateData);

        if (newInvoiceObject?.documentId) {
            triggerToast({
                message: `Estimate ${customLabel} Converted`,
                action: 'saved',
                icon: <ConvertIconWhite className={classes.convertIcon} />
            });

            setTimeout(() => {
                navigate(`/invoice/${newInvoiceObject.documentId}`);
            }, 1000);
        }
        return newInvoiceObject;
    };
};
